import type { CSSProperties, ReactNode } from 'react'
import { useLingui } from '@lingui/react/macro'
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useIndicators } from '@/hooks/use-indicators'
import { Avatar } from '@/routes/_app/-components/shared/avatar'
import { getIndicatorsColor } from '@/lib/utils'
import { IndicatorCompetencesIcon } from '@/routes/_app/-components/assets/indicators-competences-icon'
import { ScenePage, Screen } from './browser-frame'

const DIM = ['var(--color-radar-blue)', 'var(--color-radar-green)', 'var(--color-radar-yellow)']
const dimOf = (index: number) => Math.floor(index / 6)
const dimColor = (index: number) => DIM[dimOf(index)] ?? 'var(--color-radar-blue)'

function useCatalog() {
  const { indicators } = useIndicators()
  return {
    names: indicators.competences.competences.map((c) => c.name),
    dimensions: indicators.competences.dimensions.map((d) => d.name),
  }
}

// MARK: atoms

function Btn(props: { children: ReactNode; ghost?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1 rounded-md px-2 text-[9px] font-medium whitespace-nowrap [&_svg]:size-2.5',
        props.ghost ? 'text-foreground' : 'bg-(--button-fill) text-(--button-foreground)',
      )}
    >
      {props.children}
    </span>
  )
}

function DimensionHead(props: { index: number; name: string }) {
  return (
    <div className="flex items-center gap-1 px-1">
      <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: dimColor(props.index * 6) }} />
      <span className="text-muted-foreground truncate text-[7px] font-bold tracking-wider uppercase">{props.name}</span>
    </div>
  )
}

function Columns(props: { dimensions: string[]; row: (index: number) => ReactNode; className?: string }) {
  return (
    <div className={cn('grid grid-cols-3 gap-2 px-3 py-2', props.className)}>
      {[0, 1, 2].map((c) => (
        <div key={c} className="flex min-w-0 flex-col gap-1">
          <DimensionHead index={c} name={props.dimensions[c] ?? ''} />
          {[0, 1, 2, 3, 4, 5].map((r) => (
            <div key={r}>{props.row(c * 6 + r)}</div>
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * The score bar the report draws: value over 12, tinted by dimension, growing in at `at`. `scene-grow-hold` keeps it
 * at 0 until it grows and resets it off screen, so a loop never shows full, empty, full.
 */
function ScoreBar(props: { value: number; color: string; at: number }) {
  return (
    <span className="bg-muted block h-1.5 flex-1 overflow-hidden rounded-full">
      <span
        className="scene-grow-hold block h-full rounded-full"
        style={{ width: `${(props.value / 12) * 100}%`, backgroundColor: props.color, animationDelay: `${props.at}s` }}
      />
    </span>
  )
}

/** The report's histogram: three dimension columns on a window, one stacked column (`narrow`) on a phone. */
function ReportRows(props: { names: string[]; dimensions: string[]; scores: number[]; at: number; narrow?: boolean }) {
  return (
    <Columns
      dimensions={props.dimensions}
      className={cn('px-0 py-0', props.narrow && 'grid-cols-1 gap-1.5')}
      row={(i) => (
        <div className="flex items-center gap-1.5 px-1">
          <span className={cn('truncate text-[8px] font-medium', props.narrow ? 'w-24' : 'w-12')}>
            {props.names[i]}
          </span>
          <ScoreBar value={props.scores[i] ?? 0} color={dimColor(i)} at={props.at + i * 0.05} />
          <span className="text-muted-foreground w-3 text-right text-[7px] tabular-nums">{props.scores[i]}</span>
        </div>
      )}
    />
  )
}

const PERSON = [9, 7, 8, 5, 3, 2, 10, 8, 6, 7, 5, 4, 8, 6, 3, 9, 7, 5]

// MARK: the test

/** The rank glyph of test-rank-item.tsx: a primary plus on top, a neutral middle, a destructive minus at the bottom. */
function RankGlyph(props: { position: number }) {
  return (
    <span
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded-full',
        props.position === 0 && 'bg-primary/25 text-primary',
        props.position === 1 && 'bg-muted',
        props.position === 2 && 'bg-destructive/15 text-destructive',
      )}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        className="size-2.5"
      >
        {props.position === 0 && <path d="M5 12h14M12 5v14" />}
        {props.position === 2 && <path d="M5 12h14" />}
      </svg>
    </span>
  )
}

/**
 * One rank row of the test, as test-rank-item.tsx draws it: grip, rank glyph, statement, indicator icon. With `touch`,
 * the glyph swaps to `overlayPosition` on the finger's timeline (`scene-touch-swap`), which also takes it back before
 * the page returns.
 */
function RankRow(props: {
  position: number
  text: string
  indicator: number
  overlayPosition?: number
  // The drag take (touch scene) whose drop swaps this glyph.
  touch?: 1 | 2
}) {
  return (
    <>
      <span className="flex h-2.5 w-1.5 shrink-0 flex-col justify-between">
        {[0, 1, 2].map((dot) => (
          <span key={dot} className="flex justify-between">
            <span className="bg-muted-foreground/40 size-0.5 rounded-full" />
            <span className="bg-muted-foreground/40 size-0.5 rounded-full" />
          </span>
        ))}
      </span>
      {props.overlayPosition === undefined || !props.touch ? (
        <RankGlyph position={props.position} />
      ) : (
        // A crossfade, not an overlay: the new glyph's tint is translucent, so the old one must leave as it arrives.
        <span className="grid *:[grid-area:1/1]">
          <span className={`scene-touch-unswap-${props.touch}`}>
            <RankGlyph position={props.position} />
          </span>
          <span className={`scene-touch-swap-${props.touch}`}>
            <RankGlyph position={props.overlayPosition} />
          </span>
        </span>
      )}
      <span className="line-clamp-3 min-w-0 flex-1 text-[8px] leading-tight font-medium">{props.text}</span>
      <IndicatorCompetencesIcon
        type="competences"
        index={props.indicator}
        colors={getIndicatorsColor('competences', props.indicator)}
        size={22}
      />
    </>
  )
}

/**
 * A neutral hand silhouette with the index finger out, fingertip at the origin of its zero-size box. No skin tone and no
 * cursor styling, and a white halo so it reads on a light or a dark screen.
 */
function HandGlyph() {
  return (
    <svg
      viewBox="0 0 20 28"
      aria-hidden
      className="absolute -top-px -left-[7.4px] h-[28px] w-[20px] overflow-visible [filter:drop-shadow(0_0_0.7px_#fff)_drop-shadow(0_0_0.7px_#fff)_drop-shadow(0_2px_3px_rgb(0_0_0/0.35))]"
    >
      <g fill="#2a2a2a">
        <rect x="5.5" y="1" width="3.8" height="13" rx="1.9" />
        <path d="M3.6 13.2c0-1.4 1-2.4 2.4-2.4h8.2c1.6 0 2.8 1.2 2.8 2.8v4.6c0 4.4-2.8 8.3-7 8.3h-.6c-3.4 0-5.8-2.8-5.8-6.4z" />
        <rect x="2.4" y="14.9" width="2.4" height="2.8" rx="1.2" transform="rotate(-28 3.6 16.3)" />
      </g>
    </svg>
  )
}

/** The finger that drags a row: it rides inside the row, so it never drifts from what it holds. `take` picks its timeline. */
function TouchHand(props: { take: 1 | 2 }) {
  return (
    <span aria-hidden className="pointer-events-none absolute top-1/2 right-16 z-20 size-0">
      <span
        className={cn(
          `scene-touch-ripple-${props.take}`,
          'bg-foreground/15 ring-foreground/30 absolute -top-2 -left-2 size-4 rounded-full ring-1',
        )}
      />
      <span className={cn(`scene-touch-${props.take}`, 'absolute block origin-top-left')}>
        <HandGlyph />
      </span>
    </span>
  )
}

/** The same hand tapping the button it sits on: in, one press, out (`scene-tap`, on the landing phone's 12 s clock). */
function TapHand() {
  return (
    <span aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 z-20 size-0">
      <span className="scene-tap-ripple bg-foreground/15 ring-foreground/30 absolute -top-2 -left-2 size-4 rounded-full ring-1" />
      <span className="scene-tap absolute block origin-top-left">
        <HandGlyph />
      </span>
    </span>
  )
}

// `n` rows down (up when negative), a row being its own height plus the 0.375rem gap.
const slot = (n: number): CSSProperties => ({
  '--to-y': `calc(${n * 100}% ${n < 0 ? '-' : '+'} ${Math.abs(n) * 0.375}rem)`,
})

// Which of the two progress steps an element belongs to, on the step-swap timeline.
const step = (index: number) => (index === 0 ? 'scene-step-old' : 'scene-step-new')

/**
 * The last two triads of the competencies test: a finger drags triad 35's top statement down one and taps Next, then
 * drags triad 36's bottom statement up to first and taps Finish.
 */
function TriadScreen() {
  const { t } = useLingui()
  const statements = [
    { indicator: 10, text: t`I value and reward dedication and effort` },
    { indicator: 15, text: t`I am a person committed to my work` },
    { indicator: 5, text: t`Monitoring is one of my abilities` },
  ]
  // The last triad, which slides in once Next is tapped.
  const last = [
    { indicator: 3, text: t`I plan my work before I start` },
    { indicator: 17, text: t`I adapt easily to change` },
    { indicator: 6, text: t`I enjoy leading a team` },
  ]
  const ROW = 'bg-card border-border flex h-10 items-center gap-1.5 rounded-lg border-2 px-2 select-none'
  // 35 of 36, then 36 of 36, whose button reads Finish.
  const steps = [
    { label: '35 / 36', percent: 97 },
    { label: '36 / 36', percent: 100 },
  ]
  return (
    <>
      <div className="flex flex-col items-center gap-0.5 px-3 pt-3 text-center">
        <div className="text-[12px] font-semibold">{t`Competencies test`}</div>
        <div className="text-muted-foreground text-[8px]">{t`Your knowledge, skills and attitudes`}</div>
        <div className="text-muted-foreground/80 text-[7px]">{t`Drag the statements to rank them by your level of preference`}</div>
      </div>
      {/* Triad 35 is ranked and slides out as triad 36 slides in to be ranked; the panels share one cell of the grid.
          The slide needs `overflow-hidden`, so the bottom padding (given back by the negative margin) leaves room for
          the finger and the lifted shadow of the last row instead of cutting them off. */}
      <div className="mx-auto -mb-8 grid w-full max-w-[420px] overflow-hidden px-4 pt-3 pb-9 *:[grid-area:1/1]">
        <div className="scene-slide-out flex flex-col gap-1.5">
          {/* The top statement goes down to second place; the second rises to make room. */}
          <div className={cn(ROW, 'scene-touch-drag-1 relative z-10')} style={slot(1)}>
            <RankRow position={0} overlayPosition={1} touch={1} text={statements[0]?.text ?? ''} indicator={10} />
            <TouchHand take={1} />
          </div>
          <div className={cn(ROW, 'scene-touch-shift-1')} style={slot(-1)}>
            <RankRow position={1} overlayPosition={0} touch={1} text={statements[1]?.text ?? ''} indicator={15} />
          </div>
          <div className={ROW}>
            <RankRow position={2} text={statements[2]?.text ?? ''} indicator={5} />
          </div>
        </div>
        <div className="scene-slide-in flex flex-col gap-1.5">
          {/* The bottom statement goes up to first place; the other two step down one. */}
          <div className={cn(ROW, 'scene-touch-shift-2')} style={slot(1)}>
            <RankRow position={0} overlayPosition={1} touch={2} text={last[0]?.text ?? ''} indicator={3} />
          </div>
          <div className={cn(ROW, 'scene-touch-shift-2')} style={slot(1)}>
            <RankRow position={1} overlayPosition={2} touch={2} text={last[1]?.text ?? ''} indicator={17} />
          </div>
          <div className={cn(ROW, 'scene-touch-drag-2 relative z-10')} style={slot(-2)}>
            <RankRow position={2} overlayPosition={0} touch={2} text={last[2]?.text ?? ''} indicator={6} />
            <TouchHand take={2} />
          </div>
        </div>
      </div>
      <div className="mx-auto mt-auto flex w-full max-w-[420px] flex-col gap-1.5 px-4 pb-3">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground grid text-[7px] tabular-nums *:[grid-area:1/1]">
            {steps.map((entry, index) => (
              <span key={entry.label} className={step(index)}>
                {entry.label}
              </span>
            ))}
          </span>
          <span className="bg-muted relative block h-1 flex-1 rounded-full">
            {steps.map((entry, index) => (
              <span key={entry.label} className={cn('absolute inset-0', step(index))}>
                <span
                  className="bg-primary absolute inset-y-0 left-0 rounded-full"
                  style={{ width: `${entry.percent}%` }}
                />
                <span
                  className="bg-background border-primary absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2"
                  style={{ left: `${entry.percent}%` }}
                />
              </span>
            ))}
          </span>
          <span className="text-muted-foreground grid text-[7px] tabular-nums *:[grid-area:1/1]">
            {steps.map((entry, index) => (
              <span key={entry.label} className={step(index)}>
                {entry.percent}%
              </span>
            ))}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <Btn ghost>
            <ChevronLeftIcon />
            {t`Previous`}
          </Btn>
          <span className="scene-press relative grid *:[grid-area:1/1]">
            <span className={cn('flex justify-end', step(0))}>
              <Btn>
                {t`Next`}
                <ChevronRightIcon />
              </Btn>
            </span>
            <span className={cn('flex justify-end', step(1))}>
              <Btn>
                {t`Finish`}
                <CheckIcon />
              </Btn>
            </span>
            <TapHand />
          </span>
        </div>
        <span className="text-muted-foreground/70 text-center text-[7px]">{t`Progress is saved automatically`}</span>
      </div>
    </>
  )
}

// MARK: the report

/** The professional's own report on the phone: their name, then the histogram growing in at `at`. */
function ProfessionalReport(props: { at: number; name: string }) {
  const { t } = useLingui()
  const { names, dimensions } = useCatalog()
  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center gap-2">
        {/* The product's own avatar: the initials on the colour it hashes from the name, as the report shows it. */}
        <Avatar
          type="member"
          name={props.name}
          size="sm"
          forceInitials
          className="size-3.5 shrink-0 rounded-full text-[8px]"
        />
        <span className="truncate text-[10px] font-semibold">{props.name}</span>
        <span className="bg-muted ml-auto rounded-full px-2 py-0.5 text-[8px] font-semibold">{t`My report`}</span>
      </div>
      <ReportRows names={names} dimensions={dimensions} scores={PERSON} at={props.at} narrow />
    </div>
  )
}

/**
 * The professional's flow for the landing, on a 16 s loop. The test (`scene-page-test`) answers the last two triads: a
 * finger ranks triad 35, taps Next, ranks triad 36 and taps Finish. The report (`scene-page-report`) crossfades in as
 * the test leaves and stays to the end of the loop, its bars growing from 0.
 */
export function ProfessionalTestScreen() {
  return (
    <Screen cycle={16}>
      <ScenePage delay={0} className="scene-page-test">
        <TriadScreen />
      </ScenePage>
      {/* Enters at 10.4 s, as the test fades out. */}
      <ScenePage delay={10.4} className="scene-page-report">
        <ProfessionalReport at={11} name="Laura Martín" />
      </ScenePage>
    </Screen>
  )
}
